import { TextureLoader, ImageBitmapLoader, Texture } from "three";
import { RethrownError } from "./errors"; // Assuming this is correctly defined elsewhere

// --- Browser Compatibility Check (similar to Three.js GLTFLoader r173+) ---
let _isImageBitmapLoaderAvailable = null;

function checkImageBitmapLoaderAvailability() {
  // Run only once
  if (_isImageBitmapLoaderAvailable !== null) {
    return _isImageBitmapLoaderAvailable;
  }

  // Basic check
  if (typeof createImageBitmap === "undefined") {
    _isImageBitmapLoaderAvailable = false;
    return false;
  }

  // More specific browser checks (optional but recommended for robustness)
  let isSafari = false;
  let safariVersion = -1;
  let isFirefox = false;
  let firefoxVersion = -1;

  if (typeof navigator !== "undefined") {
    const userAgent = navigator.userAgent;

    // Safari detection (excluding Chrome/Android) and version check
    isSafari = /^((?!chrome|android).)*safari/i.test(userAgent) === true;
    const safariMatch = userAgent.match(/Version\/(\d+)/);
    safariVersion = isSafari && safariMatch ? parseInt(safariMatch[1], 10) : -1;

    // Firefox detection and version check
    isFirefox = userAgent.indexOf("Firefox") > -1;
    const firefoxMatch = userAgent.match(/Firefox\/([0-9]+)\./);
    firefoxVersion =
      isFirefox && firefoxMatch ? parseInt(firefoxMatch[1], 10) : -1;
  }

  // Enable ImageBitmapLoader if it exists and browser isn't known problematic
  _isImageBitmapLoaderAvailable =
    !(isSafari && safariVersion < 17) && !(isFirefox && firefoxVersion < 98);
  return _isImageBitmapLoaderAvailable;
}
// --- End Browser Compatibility Check ---

// Texture loading function that returns a promise
// Uses ImageBitmapLoader when available and suitable for performance
export default function loadTexture(src, providedLoader) {
  return new Promise((resolve, reject) => {
    let loader;
    let useImageBitmapLoader = false;

    // Determine which loader to use
    if (providedLoader) {
      // User explicitly provided a loader, respect their choice
      loader = providedLoader;
      // Check if the provided loader *is* an ImageBitmapLoader
      // Note: instanceof might fail across different bundles/versions,
      // checking for a unique property like isImageBitmapLoader is safer if possible.
      if (
        loader instanceof ImageBitmapLoader ||
        loader.isImageBitmapLoader === true
      ) {
        useImageBitmapLoader = true;
      }
    } else {
      // No loader provided, create a default based on compatibility
      if (checkImageBitmapLoaderAvailability()) {
        loader = new ImageBitmapLoader();
        loader.setOptions({ imageOrientation: "flipY" });
        useImageBitmapLoader = true;
      } else {
        loader = new TextureLoader();
      }
    }

    // Define the success callback based on the loader type
    const onLoad = result => {
      let texture;
      if (useImageBitmapLoader) {
        // Result is an ImageBitmap
        texture = new Texture(result);
        texture.needsUpdate = true; // Crucial for ImageBitmap textures
      } else {
        // Result is already a Texture
        texture = result;
      }

      // Note: Color space encoding (.colorSpace) should generally be handled
      // by the material that uses the texture, based on the map type (e.g., SRGBColorSpace for map).
      // Setting it here might be premature unless you have specific needs.

      resolve(texture);
    };

    // Define the error callback
    const onError = errorEvent => {
      // errorEvent might be an Error object or an ErrorEvent
      let message = `Error loading texture "${src}"`;
      let cause = errorEvent;

      // Try to extract a more specific message/error if it's an ErrorEvent
      if (errorEvent instanceof ErrorEvent) {
        message = errorEvent.message || message;
        cause = errorEvent.error || errorEvent; // Underlying error if available
      } else if (typeof errorEvent === "string") {
        // Sometimes simple string errors are passed
        message = `${message}: ${errorEvent}`;
      }

      reject(new RethrownError(message, cause));
    };

    // Set common loader options (optional, but good practice)
    if (loader.setCrossOrigin) {
      loader.setCrossOrigin("anonymous"); // Common default
    }
    // if (loader.setRequestHeader) { loader.setRequestHeader(...) } // If needed

    // Start loading
    loader.load(
      src,
      onLoad,
      undefined /* onProgress - not typically used here */,
      onError
    );
  });
}
